import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { stylingApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import {
  formatNumber,
  formatDate,
  formatDeltaPercent,
  getDeltaDirection,
  formatDuration,
} from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business Styling Console概要ページ。スタイリングKPI(件数/成功率/平均時間/スタイリスト数)・トレンドチャート・イベント内訳を表示
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects formatDeltaPercent [calls] 差分パーセントフォーマット
 * @graph-connects getDeltaDirection [calls] 差分方向判定
 * @graph-connects formatDuration [calls] 時間フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/overview')({
  component: StylingOverviewPage,
});

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function StylingOverviewPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const kpiQuery = useQuery({
    queryKey: queryKeys.styling.kpi(),
    queryFn: () => stylingApi.getKpi(),
    staleTime: 5 * 60 * 1000,
  });

  const trendsQuery = useQuery({
    queryKey: queryKeys.styling.trends(startDate, endDate),
    queryFn: () => stylingApi.getTrends(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const eventBreakdownQuery = useQuery({
    queryKey: queryKeys.styling.eventBreakdown(startDate, endDate),
    queryFn: () => stylingApi.getEventBreakdown(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const kpi = kpiQuery.data;

  // イベントタイムラインのピボット（date → {event1: count, event2: count, ...}）
  const eventTimelineData = (() => {
    if (!eventBreakdownQuery.data) return [];
    const byDate = new Map<string, Record<string, unknown>>();
    for (const d of eventBreakdownQuery.data.data) {
      if (!byDate.has(d.date)) byDate.set(d.date, { date: d.date });
      const row = byDate.get(d.date);
      if (!row) continue;
      row[d.event] = d.eventCount;
    }
    return Array.from(byDate.values()).sort((a, b) => (String(a.date) < String(b.date) ? -1 : 1));
  })();

  const eventNames = (() => {
    if (!eventBreakdownQuery.data) return [];
    const names = new Set<string>();
    for (const d of eventBreakdownQuery.data.data) names.add(d.event);
    return Array.from(names);
  })();

  const CHART_COLORS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
    'var(--chart-6)',
    'var(--chart-7)',
    'var(--chart-8)',
  ];

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.title', 'スタイリングコンソール分析')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t('styling.description', 'スタイリスト操作の分析ダッシュボード')}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* KPI カード */}
      {kpi && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StylingKpiCard
            label={t('styling.totalStylings', 'スタイリング件数')}
            value={formatNumber(kpi.totalStylings.value)}
            delta={kpi.totalStylings.deltaPercent}
          />
          <StylingKpiCard
            label={t('styling.successRate', '成功率')}
            value={`${((kpi.successRate.value ?? 0) * 100).toFixed(1)}%`}
            delta={kpi.successRate.deltaPercent}
          />
          <StylingKpiCard
            label={t('styling.avgStylingTime', '平均スタイリング時間')}
            value={kpi.avgStylingTimeMs.value ? formatDuration(kpi.avgStylingTimeMs.value) : '-'}
            delta={kpi.avgStylingTimeMs.deltaPercent}
          />
          <StylingKpiCard
            label={t('styling.activeStylistCount', 'アクティブスタイリスト')}
            value={formatNumber(kpi.activeStylistCount.value)}
            delta={kpi.activeStylistCount.deltaPercent}
          />
        </div>
      )}

      {/* トレンドチャート */}
      {trendsQuery.data && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.dailyTrend', '日次トレンド')}
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendsQuery.data.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="totalStylings"
                  name={t('styling.totalStylings', 'スタイリング件数')}
                  stroke="var(--chart-1)"
                  fill="var(--chart-1)"
                  fillOpacity={0.1}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="failedCount"
                  name={t('styling.failedCount', '失敗件数')}
                  stroke="var(--chart-3)"
                  fill="var(--chart-3)"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* イベントタイムライン */}
      {eventTimelineData.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.eventTimeline', 'イベントタイムライン')}
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={eventTimelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                {eventNames.map((name, i) => (
                  <Area
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stackId="events"
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    fillOpacity={0.3}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function StylingKpiCard({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta: number | null;
}) {
  const direction = delta !== null ? getDeltaDirection(delta) : 'neutral';
  const colorClass =
    direction === 'positive'
      ? 'text-[var(--color-success)]'
      : direction === 'negative'
        ? 'text-[var(--color-error)]'
        : 'text-[var(--color-text-muted)]';

  return (
    <div className="card animate-fade-in-up">
      <p className="text-xs text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className="text-2xl font-bold text-[var(--color-text-primary)]">{value}</p>
      {delta !== null && (
        <p className={`text-xs mt-1 ${colorClass}`}>{formatDeltaPercent(delta)} vs 前日</p>
      )}
    </div>
  );
}
